import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { useWorkflow } from "../../context/WorkflowContext";
import { Loader2, Puzzle } from "lucide-react";
import { toast } from "sonner";
import { ScrollArea } from "@shadcn-in-nx/ui/scroll-area";
import { Switch } from "@shadcn-in-nx/ui/switch";
import { Label } from "@shadcn-in-nx/ui/label";
import IntegrationCard from "./IntegrationCard";

export default function IntegrationsPanel() {
    const { t } = useTranslation();
    const { workflow, refetchWorkflow } = useWorkflow();
    const [integrations, setIntegrations] = useState([]);
    const [isTokenPublic, setIsTokenPublic] = useState(true);
    const [entityFields, setEntityFields] = useState([]);

    const { call: fetchIntegrations, isLoading, error } = useBluelagoon({
        method: "getAllWorkflowIntegrations",
    });
    const { call: fetchEntitySchema } = useBluelagoon({
        method: "getWorkflowEntitySchema",
    });
    const { call: updateWorkflow, isLoading: isUpdating } = useBluelagoon({
        method: "updateWorkflow",
    });
    const { call: saveSecurity } = useBluelagoon({
        method: "updateIntegrationSecurity",
    });

    useEffect(() => {
        if (workflow?.id) {
            fetchIntegrations({ workflowId: workflow.id }).then((response) => {
                if (response?.data) setIntegrations(response.data);
            });
            fetchEntitySchema({ workflowId: workflow.id }).then((response) => {
                if (response?.data?.fields) setEntityFields(response.data.fields);
            });
        }
        if (workflow) setIsTokenPublic(workflow.isTokenPublic ?? true);
    }, [workflow, fetchIntegrations, fetchEntitySchema]);

    const handleToggleTokenPublic = async (checked) => {
        setIsTokenPublic(checked);
        try {
            await updateWorkflow({
                id: workflow.id,
                userId: workflow.userId,
                isTokenPublic: checked,
            });
            if (refetchWorkflow) refetchWorkflow();
            toast.success(t("integrationsPanel.settingsUpdated") || "Settings updated");
        } catch (err) {
            setIsTokenPublic(!checked);
            toast.error(t("integrationsPanel.updateError") || "Failed to update settings");
        }
    };

    const handleSecurityChange = useCallback(async (integrationId, rule) => {
        try {
            await saveSecurity({
                workflowId: workflow.id,
                integrationId,
                securityRule: rule,
            });
            setIntegrations((prev) =>
                prev.map((item) =>
                    item.integrationId === integrationId
                        ? { ...item, securityRule: rule }
                        : item
                )
            );
            toast.success(t("integrationsPanel.securityUpdated") || "Security updated");
        } catch (err) {
            toast.error(t("integrationsPanel.updateError") || "Failed to update settings");
        }
    }, [workflow, saveSecurity, t]);

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-full items-center justify-center p-6 text-center">
                <div className="space-y-2">
                    <p className="text-sm font-medium text-destructive">
                        {t("integrationsPanel.errorLoading") || "Error loading integrations"}
                    </p>
                    <p className="text-xs text-muted-foreground">{error.message}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col bg-background">
            <div className="border-b p-4 space-y-4">
                <div>
                    <h2 className="text-lg font-semibold">
                        {t("integrationsPanel.title") || "Integrations"}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        {t("integrationsPanel.description") || "Manage integrations connected to this workflow."}
                    </p>
                </div>
                <div className="flex items-center justify-between space-x-2 rounded-lg border p-4 shadow-sm">
                    <div className="space-y-0.5">
                        <Label htmlFor="token-public" className="text-base font-medium">
                            {t("integrationsPanel.tokenAccessTitle") || "Integration Access Token"}
                        </Label>
                        <p className="text-sm text-muted-foreground max-w-[90%]">
                            {isTokenPublic
                                ? t("integrationsPanel.tokenPublicDesc") || "When open it allows any external to use any integration you have in your account."
                                : t("integrationsPanel.tokenPrivateDesc") || "When disabled it only allows the integrations that record while using your app."}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 italic">
                            {t("integrationsPanel.tokenWarning") || "You should disable once you are ready and won't be adding more integrations."}
                        </p>
                    </div>
                    <Switch id="token-public" checked={isTokenPublic} onCheckedChange={handleToggleTokenPublic} disabled={isUpdating} />
                </div>
            </div>
            <ScrollArea className="flex-1 p-4">
                {integrations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="mb-4 rounded-full bg-muted p-3">
                            <Puzzle className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <h3 className="text-sm font-medium">
                            {t("integrationsPanel.noIntegrations") || "No integrations yet"}
                        </h3>
                        <p className="mt-1 max-w-xs text-xs text-muted-foreground">
                            {t("integrationsPanel.noIntegrationsDesc") || "Integrations will appear here once you start using the app."}
                        </p>
                    </div>
                ) : (
                    <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2">
                        {integrations.map((item) => (
                            <IntegrationCard key={item.id} item={item} onSecurityChange={handleSecurityChange} entityFields={entityFields} />
                        ))}
                    </div>
                )}
            </ScrollArea>
        </div>
    );
}
